/// <reference path="../../../../typings/index.d.ts" />
/// <reference path="inventoryCount.module.ts" />
/// <reference path="../../components/common/lotModalBridge.helper.ts" />

module Meevo.Plugins.InventoryCount {

    /**
     * Input shape for the <meevo-lot-tracking-modal> custom element's `lineItem`
     * property when invoked from the Inventory Count flow. Mirrors the web
     * component's `InventoryCountLineItem` arm of the `LotTrackingLineItem`
     * discriminated union (see Web.Hub web-components/.../models/inventory-count-line-item.ts).
     *
     * `context: 'inventory-count'` is the union discriminator — required so
     * the modal's internal type-narrowing knows which arm it has.
     */
    export interface IInventoryCountLotModalInput {
        context: 'inventory-count';
        variantId: string;
        productInventoryTypeEnum: number;
        productDisplayName: string;
        variantDisplayName: string;
        sku: string;
        barCode: string;
        inventoryTypeDisplay: string;
        trackExpirationDate: boolean;
        onHand: number;
        eachContains: boolean;
        unitOfMeasureQuantity?: number;
        unitOfMeasureLabel?: string;
        /** WI 121230: parent count is a conflict count → modal renders ⚠ on uncounted lots. */
        isConflictCount: boolean;
        /** WI 117621: identifies which floor (if any) the modal is scoped to.
         *  When the inventory count's floor filter is active, the host
         *  pre-filters existingAllocations to that floor's per-floor lot
         *  rows and stamps every emitted allocation with this id so the
         *  save-side per-floor router knows where the data belongs. Null
         *  when the count is in "All Locations" mode — host keeps the
         *  existing variant-aggregate write path. Mirrors the modal-side
         *  InventoryCountLineItem.floorLocationId. */
        floorLocationId?: string | null;
        /** Target count for this variant when the line item originates from the
         *  scan-driven batch flow. The batch modal's per-variant section enforces
         *  assigned === target before the variant can be marked complete. This is
         *  the composite total — whole + partial/unitOfMeasureQuantity for
         *  Each-Contains variants, a whole integer otherwise — compared with an
         *  epsilon tolerance since the partial fraction is a float. Undefined for
         *  the grid-pencil free-form flow, where there is no scanned target to
         *  reconcile against. */
        scannedTarget?: number;
    }

    /**
     * Allocation shape for both `existingAllocations` (modal input) and the
     * `saved` event payload. Mirrors the web component's
     * `InventoryCountAllocation` wire shape:
     *   - `wholeQuantity` is used for whole-unit-only variants.
     *   - `wholeRows` / `partialRows` are per-row arrays for Each Contains
     *     variants. The host collapses these into aggregate `actualCount` /
     *     `actualCountUnits` totals on save (per-lot rollup) and regenerates
     *     a single whole row + single partial row on reopen.
     *   - `isNewLot` flags allocations whose lot row was created in this
     *     modal session (vs. an existing variant lot picked from typeahead).
     */
    export interface IInventoryCountAllocation {
        variantLot_TId: string;
        lotNumber: string;
        expirationDate: string | null;
        isNewLot?: boolean;
        /** Per-lot system on-hand snapshot, surfaced in the modal's named-lot-card
         *  header alongside lot number + expiration so the user sees what the
         *  system believes is on the shelf for this lot before counting. Optional
         *  — host context omits it when no prior snapshot exists (e.g. lot created
         *  mid-count); the modal hides the on-hand line in that case. */
        onHand?: number;
        /** Drives the modal's named-lot-card vs. add-lot view branching. Host
         *  sets this true only when the underlying lot has been server-Saved
         *  (gated on lot.startOnHand being non-null, per the C# model). Lots
         *  that were Applied from a prior modal session but haven't yet been
         *  count-Saved come back as false so the modal keeps the editable
         *  view (and avoids displaying the unrefreshed currentOnHand=0)
         *  until the next GET reload. */
        isSavedLot?: boolean;
        /** WI 121230: true when this lot was counted in the *original*
         *  count whose commit triggered this conflict. Drives the modal's
         *  conflict-count ⚠ indicator (renders only when false). Distinct
         *  from isSavedLot (server-persisted view-branching flag) — both
         *  pre-existing and auto-pulled-into-conflict lots are persisted,
         *  so isSavedLot can't distinguish them. The host reads this from
         *  the server's per-lot IsCounted column (the conflict-start SP
         *  sets IsCounted=1 on copied-from-original lots, 0 on missing
         *  lots auto-detected via on-hand drift). Modal echoes back on
         *  emit unchanged. Optional — non-conflict-count flows ignore. */
        isCountedInOriginal?: boolean;
        wholeQuantity?: number;
        wholeRows?: number[];
        partialRows?: number[];
        /** WI 117621: floor scope for this allocation. Set by the host
         *  (mirrored from the modal input) when the inventory count's
         *  floor filter is active so the save-side host code can route
         *  per-floor lot counts onto the matching
         *  productVariantsPerFloorLocation[i].lots collection. Null for
         *  "All Locations" mode — host keeps the variant-aggregate write
         *  path in that case. Mirrors the modal-side
         *  InventoryCountAllocation.floorLocationId. */
        floorLocationId?: string | null;
    }

    /**
     * Thin AngularJS bridge for the <meevo-lot-tracking-modal> custom element,
     * scoped to the Inventory Count flow. All host-bridge construction, session
     * teardown wiring, and custom-element-registration logic lives in
     * `Meevo.Components.Common.LotModalBridgeHelper`. This wrapper supplies the
     * `context: 'inventory-count'` literal and the per-flow line-item /
     * allocation types.
     *
     * Inventory Count only ever uses `editable` or `readonly` modes — there is
     * no `partial` flow (lots are committed atomically with the count, not
     * incrementally like PO receivals).
     */
    export class LotModalBridge {

        static $inject = [
            'meevo.components.common.LotModalBridgeHelper',
        ];

        constructor(
            private helper: Meevo.Components.Common.LotModalBridgeHelper,
        ) { }

        /**
         * Public API: mount the lot-tracking-modal for the given inventory
         * count line item.
         *
         * @param lineItem              Variant + count-context info (on-hand, each-contains, etc.)
         * @param existingAllocations   Pre-existing per-lot allocations for the variant
         * @param mode                  'editable' if the count header is open; 'readonly' once committed
         * @param onSaved               Invoked with the final allocations when the user saves
         * @param onCancelled           Invoked when the user cancels (X, Escape, or Cancel)
         * @param hideOnHand            When true, the modal suppresses on-hand display
         *                              (header subtitle + per-lot cards). Mirrors the host's
         *                              `INVENTORY_COUNT_FIELD_ONHAND_READ` security-key gate
         *                              so users without Grant access see the same redaction
         *                              in the modal as in the count grid. Default false.
         */
        public open(
            lineItem: IInventoryCountLotModalInput,
            existingAllocations: IInventoryCountAllocation[],
            mode: 'editable' | 'readonly',
            onSaved?: (allocations: IInventoryCountAllocation[]) => void,
            onCancelled?: () => void,
            hideOnHand?: boolean,
        ): void {
            this.helper.openModal<IInventoryCountLotModalInput, IInventoryCountAllocation>({
                context: 'inventory-count',
                mode: mode,
                lineItem: lineItem,
                domProps: {
                    existingCountAllocations: existingAllocations,
                    hideOnHand: !!hideOnHand,
                },
                onSaved: onSaved,
                onCancelled: onCancelled,
            });
        }

        /**
         * Public API: mount the batch lot-tracking modal for a set of scanned line
         * items. Used by the scan-driven inventory-count flow where the user scans
         * a barcode and assigns lots across many variants in a single modal session,
         * vs. `open()`'s one-variant grid-pencil flow.
         *
         * @param entries     Per-variant line item + its pre-existing allocations. Each
         *                    line item's `scannedTarget` drives the section's
         *                    assigned === target completion gate.
         * @param onSavedAll  Invoked once on save with the per-variant results array.
         * @param onCancelled Invoked when the user cancels the whole batch.
         * @param hideOnHand  When true, the modal suppresses on-hand display across all
         *                    sections (mirrors the `INVENTORY_COUNT_FIELD_ONHAND_READ`
         *                    security-key gate). Default false.
         * @param hadNonLot   When true, at least one scanned variant was non-lot-tracked;
         *                    surfaced in the batch modal's messaging. Default false.
         */
        public openBatch(
            entries: { lineItem: IInventoryCountLotModalInput; existingAllocations: IInventoryCountAllocation[] }[],
            onSavedAll?: (results: { variantId: string; productInventoryType: number; allocations: IInventoryCountAllocation[] }[]) => void,
            onCancelled?: () => void,
            hideOnHand?: boolean,
            hadNonLot?: boolean,
        ): void {
            this.helper.openBatchModal<IInventoryCountLotModalInput, IInventoryCountAllocation, { variantId: string; productInventoryType: number; allocations: IInventoryCountAllocation[] }>({
                entries: entries,
                domProps: { hideOnHand: !!hideOnHand, hadNonLot: !!hadNonLot },
                onSavedAll: onSavedAll,
                onCancelled: onCancelled,
            });
        }
    }

    angular.module('meevo.plugins.inventoryCount')
        .service('meevo.plugins.inventoryCount.LotModalBridge', LotModalBridge);
}
